/**
 * Common - Product thumbnails module organism
 */

// Imports
import {
  isEmpty,
  value,
  stripHTML
} from '../Utils';
import {
  triggerProductThumbnailClickedEvent,
} from '../Events';
import {
  removeItem as removeItemFromWishlist,
} from '../Wishlist';

// Constants (classes)
const moduleClass = 'o-product-thumbnails-module';
const productThumbnailLinkClass = 'o-product-thumbnails-block__link';
const productThumbnailViewDetailsLinkClass = 'js-product-thumbnail-view-details';
const btnRemoveFromWishlistClass = 'js-remove-product-wishlist';

// Constants (selectors)
const moduleSelectorHidden = '.o-product-thumbnails-module__item--hidden';

// Constants (attributes)
const dataNameAttr = 'data-name';
const dataPriceAttr = 'data-price';
const dataSkuAttr = 'data-OCMS-thumb-sku';
const dataListAttr = 'data-list';
const dataBrandAttr = 'data-brand';
const dataCategoryAttr = 'data-category';
const dataVariantAttr = 'data-variant';
const dataClassAttr = 'data-class';

function getItemPosition(aoItem) {
  const $item = $(aoItem);
  let position = 0;

  if ($item.length) {
    $(`.${moduleClass} [${dataSkuAttr}]`).not(moduleSelectorHidden).each((anIndex, aoElement) => {
      if ($(aoElement).is($item)) {
        position = anIndex + 1;

        return false; // break
      }

      return true; // continue
    });
  }

  return position;
}

/**
 * Gets the SKU for the given item.
 * @param  {Object} aoItem - The item.
 * @return {String} The SKU.
 */
function getItemSKU(aoItem) {
  const $item = $(aoItem);
  let sku = '';

  if ($item.length) {
    sku = value($item.attr(dataSkuAttr), '');
  }

  return sku;
}

/**
 * Gets the name for the given item.
 * @param  {Object} aoItem - The item.
 * @return {String} The name.
 */
function getItemName(aoItem) {
  const $item = $(aoItem);
  let name = '';

  if ($item.length) {
    const $nameContainer = $item.find(`.${productThumbnailLinkClass}`).filter(':first');
    if ($nameContainer.length) {
      name = $.trim($nameContainer.text());
    }
  }

  return name;
}

/**
 * Gets the price for the given item.
 * @param  {Object} aoItem - The item.
 * @return {String} The price.
 */
function getItemPrice(aoItem) {
  const $item = $(aoItem);
  let price = '';

  if ($item.length) {
    const $priceContainer = $item.find('[itemprop="price"]').filter(':first');
    if ($priceContainer.length) {
      price = value($priceContainer.attr('content'), '');
    }
  }

  return price;
}

/**
 * Gets the list name for the given item.
 * @param  {Object} aoItem - The item.
 * @return {String} The list name.
 */
function getItemListName(aoItem) {
  const $item = $(aoItem);
  let listName = '';

  if ($item.length) {
    listName = value($item.attr(dataListAttr), '');
  }

  return listName;
}

/**
 * Gets the brand for the given item.
 * @param  {Object} aoItem - The item.
 * @return {String} The brand.
 */
function getItemBrand(aoItem) {
  const $item = $(aoItem);
  let brand = '';

  if ($item.length) {
    brand = value($item.attr(dataBrandAttr), '');
  }

  return brand;
}

/**
 * Gets the category for the given item.
 * @param  {Object} aoItem - The item.
 * @return {String} The category.
 */
function getItemCategory(aoItem) {
  const $item = $(aoItem);
  let category = '';

  if ($item.length) {
    category = value($item.attr(dataCategoryAttr), '');
  }

  return category;
}

/**
 * Gets the variant for the given item.
 * @param  {Object} aoItem - The item.
 * @return {String} The variant.
 */
function getItemVariant(aoItem) {
  const $item = $(aoItem);
  let variant = '';

  if ($item.length) {
    variant = value($item.attr(dataVariantAttr), '');
  }

  return variant;
}

/**
 * Gets the class for the given item.
 * @param  {Object} aoItem - The item.
 * @return {String} The class.
 */
function getItemClass(aoItem) {
  const $item = $(aoItem);
  let className = '';

  if ($item.length) {
    className = value($item.attr(dataClassAttr), '');
  }

  return className;
}

/**
 * Handles the click on the button
 * meant to remove an item from the wishlist.
 * @param {Object} aoEvent - The event.
 */
function handleWishlistItemRemove(aoEvent) {
  const $target = $(aoEvent.currentTarget);
  const $item = $target.parents(`[${dataSkuAttr}]`);

  if (!$item.length) {
    return false;
  }

  const sku = $item.attr(dataSkuAttr);

  if (isEmpty(sku)) {
    return false;
  }

  // Remove the item container
  $item.fadeOut({
    duration: 1200,
    complete: () => {
      $item.remove();
    }
  });

  return removeItemFromWishlist(sku);
}

function setFromAttr($item) {
  return {
    name: $item.attr(dataNameAttr),
    price: $item.attr(dataPriceAttr),
    position: 1,
    list: $item.attr(dataListAttr),
    brand: $item.attr(dataBrandAttr),
    category: $item.attr(dataCategoryAttr),
    variant: $item.attr(dataVariantAttr),
    productClass: $item.attr(dataClassAttr)
  };
}

function setFromElement($item) {
  return {
    name: getItemName($item),
    price: getItemPrice($item),
    position: getItemPosition($item),
    list: getItemListName($item),
    brand: getItemBrand($item),
    category: getItemCategory($item),
    variant: getItemVariant($item),
    productClass: getItemClass($item)
  };
}

function handleProductThumbnailLinkClick(aoEvent) {
  const $target = $(aoEvent.currentTarget);
  const targetIsData = $target.is(`[${dataSkuAttr}]`);
  const $item = (targetIsData === true) ? $target : $target.parents(`[${dataSkuAttr}]`); // Based on current used of tagging on XCMS

  if ($item.length) {
    const sku = $item.attr(dataSkuAttr);

    if (isEmpty(sku) === false) {
      let dataProduct = (targetIsData) ? setFromAttr($item) : setFromElement($item);
      dataProduct.id = sku;
      // Strip all HTML in one hit
      dataProduct = JSON.parse(stripHTML(JSON.stringify(dataProduct)));

      triggerProductThumbnailClickedEvent(dataProduct);
    }
  }
}

/**
 * Sets event handlers.
 */
function setEventHandlers() {
  const additionnalModuleClass = '.o-content-push-hero__product-thumbnail, .o-content-push-fullwidth-sku__item, .o-content-push-3-skus__block ';

  $(document)

  // Handle "remove from wishlist" button
  .on('click', `.${btnRemoveFromWishlistClass}`, handleWishlistItemRemove)

  // Handle "product thumbnail" link
  .on('click', `.${moduleClass} .${productThumbnailLinkClass}`, handleProductThumbnailLinkClick)

  // Trigger product thumbnail clicked event when user click on "View details" link on bottom of product thumbnail
  .on('click', `.${productThumbnailViewDetailsLinkClass}`, handleProductThumbnailLinkClick)

  // Handle product thumbnail click on module M13, M03-2, M03-3 and m03-4
  .on('click', additionnalModuleClass, handleProductThumbnailLinkClick)

  // LazySizes event use to handle thumbnail/badges images together
  .on('lazyloaded', '.o-content-push-3-skus__item', (e) => {
    if (!$(e.target).hasClass('a-img--product-badge')) {
      $(e.currentTarget).removeClass('waitForImgs');
    }
  });
}

/**
 * Init
 */
function init() {
  // Set event handlers
  setEventHandlers();
}

// Named exports - Functions
export {
  init,
  dataSkuAttr,
  getItemPosition,
  getItemSKU,
  getItemName,
  getItemPrice,
  getItemListName,
  getItemBrand,
  getItemCategory,
  getItemVariant,
  getItemClass,
};

export default init;



// WEBPACK FOOTER //
// ./js/common/organism/product-thumbnails-module.js